import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import '../styles/reset.css';
import '../styles/nivaas-theme.css';
import Footer from '../components/common/Footer';
import Header from '../components/common/Header';

const API_URL = process.env.REACT_APP_API_URL || 'https://nivaas.app';

// Use the manually uploaded wide logo from public directory

// Modern isolated wrapper for the contact page (same as landing page)
const IsolatedPage = ({ children }) => {
  const cleanupRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant"
    });
  }, []);

  // Add Font Awesome if not already loaded
  useEffect(() => {
    console.log('Contact page useEffect triggered, current pathname:', window.location.pathname);

    if (!document.querySelector('link[href*="font-awesome"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }

    // Add Inter font if not already loaded
    if (!document.querySelector('link[href*="Inter"]')) {
      const linkPreconnect1 = document.createElement('link');
      linkPreconnect1.rel = 'preconnect';
      linkPreconnect1.href = 'https://fonts.googleapis.com';
      document.head.appendChild(linkPreconnect1);

      const linkPreconnect2 = document.createElement('link');
      linkPreconnect2.rel = 'preconnect';
      linkPreconnect2.href = 'https://fonts.gstatic.com';
      linkPreconnect2.crossOrigin = true;
      document.head.appendChild(linkPreconnect2);

      const linkFont = document.createElement('link');
      linkFont.rel = 'stylesheet';
      linkFont.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap';
      document.head.appendChild(linkFont);
    }

    // Store original styles for cleanup
    const originalHtmlStyle = document.documentElement.getAttribute('style');
    const originalBodyStyle = document.body.getAttribute('style');
    const originalDataTheme = document.documentElement.getAttribute('data-bs-theme');

    cleanupRef.current = {
      originalHtmlStyle,
      originalBodyStyle,
      originalDataTheme,
      originalSetAttribute: document.documentElement.setAttribute,
      originalSetProperty: document.documentElement.style.setProperty
    };

    // Clean up any existing inline styles that might interfere
    const cleanupStyles = () => {
      console.log('Running cleanup styles for contact page...');

      // Remove all inline styles from HTML and body that might interfere
      document.documentElement.removeAttribute('style');
      document.body.removeAttribute('style');
      document.documentElement.removeAttribute('data-bs-theme');

      console.log('Removed inline styles and data-bs-theme attribute');

      // Remove any existing theme style tags that might interfere
      const existingThemeStyles = document.querySelectorAll('#global-navigation-override, #theme-override-styles, #guard-dashboard-theme-styles, #sidebar-navigation-override, #sidebar-direct-styles, #app-custom-css-override');
      existingThemeStyles.forEach(style => {
        if (style && style.parentNode) {
          style.textContent = ''; // Clear content instead of removing to avoid conflicts
        }
      });

      // Force reset any CSS custom properties that might have been set
      const cssVarsToReset = [
        '--sidebar-bg-color', '--sidebar-text-color', '--sidebar-active-bg-color',
        '--bs-primary', '--bs-secondary', '--theme-primary-color', '--theme-primary',
        '--sidebar-width', '--sidebar-icon-color', '--sidebar-border-color',
        '--sidebar-hover-bg-color', '--sidebar-hover-text-color', '--block-title-color',
        '--block-title-bg-color', '--h1-color', '--h2-color', '--h3-color',
        '--text-primary-color', '--text-secondary-color', '--logo-width',
        '--theme-primary-rgb', '--bs-primary-rgb', '--bs-secondary-rgb',
        '--primary-color', '--secondary-color', '--accent-color',
        '--sidebar-active-text-color', '--sidebar-icon-color', '--block-title-bg-color'
      ];

      cssVarsToReset.forEach(cssVar => {
        document.documentElement.style.removeProperty(cssVar);
      });

      // Set a flag to prevent theme reapplication
      document.documentElement.setAttribute('data-contact-page-active', 'true');
    };

    // More aggressive cleanup with interval to catch any reapplication
    const aggressiveCleanup = () => {
      cleanupStyles();

      // Block any attempts to set inline styles on html
      document.documentElement.setAttribute = function (name, value) {
        if (name === 'style' && document.documentElement.getAttribute('data-contact-page-active') === 'true') {
          console.log('Blocked attempt to set inline style on contact page');
          return;
        }
        return cleanupRef.current.originalSetAttribute.call(this, name, value);
      };

      // Block any attempts to set CSS properties directly
      document.documentElement.style.setProperty = function (property, value) {
        if (document.documentElement.getAttribute('data-contact-page-active') === 'true') {
          console.log('Blocked attempt to set CSS property on contact page:', property, value);
          return;
        }
        return cleanupRef.current.originalSetProperty.call(this, property, value);
      };
    };

    // Apply initial cleanup
    aggressiveCleanup();

    // Set up continuous cleanup interval to catch any reapplication
    const cleanupInterval = setInterval(() => {
      if (document.documentElement.getAttribute('data-contact-page-active') === 'true') {
        const hasInlineStyle = document.documentElement.hasAttribute('style');
        if (hasInlineStyle) {
          console.log('Detected inline style reapplication, cleaning up...');
          cleanupStyles();
        }
      }
    }, 500);

    // Set up mutation observer to watch for style changes
    observerRef.current = new MutationObserver((mutations) => {
      let needsCleanup = false;

      mutations.forEach((mutation) => {
        // Check if style attribute was modified on html or body
        if ((mutation.target === document.documentElement || mutation.target === document.body) &&
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'style' || mutation.attributeName === 'data-bs-theme')) {
          needsCleanup = true;
        }

        // Check for new style elements being added
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.tagName === 'STYLE' &&
                (node.id === 'global-navigation-override' ||
                  node.id === 'theme-override-styles' ||
                  node.id === 'guard-dashboard-theme-styles' ||
                  node.id === 'sidebar-navigation-override' ||
                  node.id === 'sidebar-direct-styles' ||
                  node.id === 'app-custom-css-override')) {
                needsCleanup = true;
              }
            }
          });
        }
      });

      if (needsCleanup && document.documentElement.getAttribute('data-contact-page-active') === 'true') {
        // Immediate cleanup when mutation is detected
        console.log('Mutation detected, cleaning up styles...');
        cleanupStyles();
      }
    });

    // Start observing
    observerRef.current.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'data-bs-theme'],
      subtree: false
    });

    observerRef.current.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
      subtree: false
    });

    observerRef.current.observe(document.head, {
      childList: true,
      subtree: false
    });

    // Cleanup function to restore original styles when component unmounts
    return () => {
      // Clear the cleanup interval
      clearInterval(cleanupInterval);

      // Remove contact page flag
      document.documentElement.removeAttribute('data-contact-page-active');

      // Restore original methods
      if (cleanupRef.current && cleanupRef.current.originalSetAttribute) {
        document.documentElement.setAttribute = cleanupRef.current.originalSetAttribute;
      }
      if (cleanupRef.current && cleanupRef.current.originalSetProperty) {
        document.documentElement.style.setProperty = cleanupRef.current.originalSetProperty;
      }

      // Disconnect observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (cleanupRef.current) {
        // Restore original styles
        if (cleanupRef.current.originalHtmlStyle) {
          document.documentElement.setAttribute('style', cleanupRef.current.originalHtmlStyle);
        } else {
          document.documentElement.removeAttribute('style');
        }

        if (cleanupRef.current.originalBodyStyle) {
          document.body.setAttribute('style', cleanupRef.current.originalBodyStyle);
        } else {
          document.body.removeAttribute('style');
        }

        if (cleanupRef.current.originalDataTheme) {
          document.documentElement.setAttribute('data-bs-theme', cleanupRef.current.originalDataTheme);
        } else {
          document.documentElement.removeAttribute('data-bs-theme');
        }
      }
    };
  }, []);

  return (
    <div className="modern-landing-wrapper">
      {children}
    </div>
  );
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters long';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
      newErrors.name = 'Name can only contain letters and spaces';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (formData.email.length > 254) {
      newErrors.email = 'Email address is too long';
    }

    // Phone validation (optional but if provided, must be valid)
    if (formData.phone.trim() && !/^[+]?[(]?[\d\s\-()]{7,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Company validation (optional but if provided, should be reasonable)
    if (formData.company.trim() && formData.company.trim().length < 2) {
      newErrors.company = 'Company name must be at least 2 characters long';
    }

    // Subject validation (optional but if provided, should be reasonable)
    if (formData.subject.trim() && formData.subject.trim().length < 3) {
      newErrors.subject = 'Subject must be at least 3 characters long';
    } else if (formData.subject.trim().length > 100) {
      newErrors.subject = 'Subject must be less than 100 characters';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    } else if (formData.message.trim().length > 1000) {
      newErrors.message = 'Message must be less than 1000 characters';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API_URL}/api/contact/submit`, {
        ...formData,
        timestamp: new Date().toISOString()
      });

      console.log('Contact form submitted successfully:', response.data);
      setSubmitStatus('success');

      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
      });

    } catch (error) {
      console.error('Error submitting contact form:', error);
      const errorMessage = error.response?.data?.message
        || 'Something went wrong. Please try again or email us directly.';
      setSubmitStatus('error');
      setStatusMessage(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <IsolatedPage>
      <div className="modern-landing">
        {/* Navigation Header - Using reusable Header component */}
        <Header
          activeSection="contact"
          showSectionLinks={true}
          onSectionClick={(sectionId) => {
            if (sectionId === 'home') {
              window.location.href = '/landing#home';
            } else {
              window.location.href = `/landing#${sectionId}`;
            }
          }}
        />

        {/* Contact Section Using Landing Page Structure */}
        <section className="minimal-contact-section">
          <div className="minimal-container">
            {/* Header */}
            <div className="minimal-header">
              <h1>Get in Touch</h1>
              <p>Have questions about Nivaas? We&apos;d love to hear from you.</p>
            </div>

            {/* Content */}
            <div className="minimal-content">
              {/* Contact Info */}
              <div className="minimal-info">
                <div className="info-item">
                  <i className="fas fa-envelope"></i>
                  <div>
                    <strong>Email</strong>
                    <span>kudosta@gmail.com</span>
                  </div>
                </div>

                <div className="info-item">
                  <i className="fas fa-phone"></i>
                  <div>
                    <strong>Phone</strong>
                    <span>+91-75975-44999</span>
                  </div>
                </div>

                <div className="info-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <div>
                    <strong>Location</strong>
                    <span>Jaipur, Rajasthan</span>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="minimal-form">
                <form onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-field">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your Name *"
                        className={errors.name ? 'error' : ''}
                      />
                      {errors.name && (
                        <div className="error-text">{errors.name}</div>
                      )}
                    </div>

                    <div className="form-field">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Your Email *"
                        className={errors.email ? 'error' : ''}
                      />
                      {errors.email && (
                        <div className="error-text">{errors.email}</div>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Phone Number"
                        className={errors.phone ? 'error' : ''}
                      />
                      {errors.phone && (
                        <div className="error-text">{errors.phone}</div>
                      )}
                    </div>

                    <div className="form-field">
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Company/Society"
                        className={errors.company ? 'error' : ''}
                      />
                      {errors.company && (
                        <div className="error-text">{errors.company}</div>
                      )}
                    </div>
                  </div>

                  <div className="form-row form-field">
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="Subject"
                      className={errors.subject ? 'error' : ''}
                    />
                    {errors.subject && (
                      <div className="error-text">{errors.subject}</div>
                    )}
                  </div>

                  <div className="customGap form-row form-field">
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Your Message *"
                      rows="6"
                      className={errors.message ? 'error' : ''}
                    ></textarea>
                    <div className={`d-flex flex-wrap ${errors.message ? 'justify-content-between' : 'justify-content-end'}`}>
                      {errors.message && (
                        <div className="error-text">{errors.message}</div>
                      )}
                      <div className={`character-count ${formData.message.length > 1000 ? 'error' :
                        formData.message.length > 900 ? 'warning' : ''
                        }`}>
                        {formData.message.length}/1000 characters
                      </div>
                    </div>

                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane"></i> Send Message
                      </>
                    )}
                  </button>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="status-message success">
                      <i className="fas fa-check-circle"></i>
                      Message sent successfully! We&apos;ll get back to you soon.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="status-message error">
                      <i className="fas fa-exclamation-triangle"></i>
                      {statusMessage}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Footer - Same as Landing Page */}
        <Footer />
      </div>
    </IsolatedPage>
  );
};

export default ContactPage; 